import { useState } from "react"

import {
  useConnectAgentPlatform,
  useDisconnectAgentPlatform,
  useGetAgentPlatformConnections,
  usePreInstallAgentPlatform,
  useUpdateAgentPlatform
} from "@/api/useDeployments"
import { isAiDiscoveryMock } from "@/domain/user"
import {
  getDetailMessage,
  isConflictError,
  isServerError
} from "@/helpers/error.helper"
import { useTranslation } from "@/hooks/useTranslation"
import { useFeatureFlags } from "@/hooks/useUser"
import { DeleteConfirmModal, useConfirmModal } from "@/molecules/ConfirmModal"
import { DEMO_PRE_INSTALL } from "@/pages/DeploymentsPage/application/useAgentPlatform.demo"

import type {
  AgentPlatformConnectRequest,
  AgentPlatformKey,
  AgentPlatformTypes
} from "@/api/useDeployments.types"

// A failed request's reason for the user: the API's detail, else by status
const useErrorMessage = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "deployments.cloudConnection.errors"
  })

  return (error: unknown) => {
    const detail = getDetailMessage(error)

    if (detail) return detail
    if (isConflictError(error)) return t("alreadyConnected")

    return t("general")
  }
}

// The demo walks through the steps with demo values: nothing is read from or
// written to kosmos-api, and nothing can be connected
export const useIsAgentPlatformDemo = () =>
  isAiDiscoveryMock(useFeatureFlags("aiDiscovery"))

// Read by one observer at a time (a platform's tab, or the settings opened
// from a deployments row): a second observer of a failed read would refetch it
// on mount and put the first back to loading
export const useAgentPlatformConnections = <P extends AgentPlatformKey>(
  platform: P,
  enabled = true
) => {
  const isDemo = useIsAgentPlatformDemo()
  const { response, isLoading, isError, error, refetch } =
    useGetAgentPlatformConnections(platform, enabled && !isDemo)
  const connections = response?.connections ?? []

  // Connections that loaded before and now fail on the server can be retried;
  // any other failure, such as an API without the route, means the cloud
  // connection is not available here
  const isLoadError = isError && !!response && isServerError(error)

  return {
    connections,
    isLoading,
    isConnected: connections.length > 0,
    isLoadError,
    isUnavailable: isError && !isLoadError,
    refetch
  }
}

// The connection's writes, each with the reason it failed
export const useAgentPlatform = <P extends AgentPlatformKey>(platform: P) => {
  type Types = AgentPlatformTypes[P]

  const preInstallMutation = usePreInstallAgentPlatform(platform)
  const connectMutation = useConnectAgentPlatform(platform)
  const updateMutation = useUpdateAgentPlatform(platform)
  const [error, setError] = useState<string>()
  const getErrorMessage = useErrorMessage()
  const isDemo = useIsAgentPlatformDemo()
  // The demo pre-installs from its own values; no longer idle once it has,
  // so the callers waiting for idle stop as they do once the request ran
  const [isDemoPreInstalled, setDemoPreInstalled] = useState(false)

  // Each request clears the previous error; a failed one resolves undefined
  // and shows its own
  const run =
    <A extends unknown[], R>(fn: (...args: A) => Promise<R>) =>
    async (...args: A): Promise<R | undefined> => {
      setError(undefined)

      try {
        return await fn(...args)
      } catch (err) {
        setError(getErrorMessage(err))

        return undefined
      }
    }

  return {
    isDemo,
    error,
    clearError: () => setError(undefined),
    preInstall: run(
      async (settings: Types["settings"]): Promise<Types["preInstall"]> => {
        if (!isDemo) return preInstallMutation.mutateAsync(settings)

        setDemoPreInstalled(true)

        return DEMO_PRE_INSTALL[platform]
      }
    ),
    isPreInstalling: preInstallMutation.isPending,
    isPreInstallIdle: isDemo ? !isDemoPreInstalled : preInstallMutation.isIdle,
    // The demo writes nothing to kosmos-api, whichever control asks
    connect: run(async (request: AgentPlatformConnectRequest<P>) => {
      if (isDemo) return undefined

      await connectMutation.mutateAsync(request)

      return true
    }),
    isConnecting: connectMutation.isPending,
    update: run(async (connectionId: string, settings: Types["settings"]) => {
      if (isDemo) return undefined

      await updateMutation.mutateAsync({
        needle: `/${encodeURIComponent(connectionId)}`,
        data: settings
      })

      return true
    }),
    isUpdating: updateMutation.isPending
  }
}

// Disconnecting a connection asks first; the platform's others are kept
export const useDisconnectConfirm = (platform: AgentPlatformKey) => {
  const remover = useDisconnectAgentPlatform(platform)
  const { confirm } = useConfirmModal()
  const { t } = useTranslation()
  const { t: tDisconnect } = useTranslation("translation", {
    keyPrefix: "deployments.cloudConnection.disconnect"
  })

  return (connectionId: string, name: string) =>
    confirm((attr) =>
      DeleteConfirmModal({
        name,
        entityName: platform,
        content: tDisconnect("guard", { name }),
        okText: t("general.disconnect"),
        messages: {
          success: tDisconnect("success", { name }),
          error: tDisconnect("error", { name })
        },
        onOk: () => remover.mutateAsync(encodeURIComponent(connectionId)),
        ...attr
      })
    )
}
